import type { CSSProperties } from "react";
import type {
  PageBackgroundMesh,
  PageBackgroundPattern,
  PageBackgroundStyle,
} from "@/cms/appearance";
import type { CMSData, CmsPage } from "@/cms/models";
import { record } from "@/purpose-sections/model";
import {
  isCssColour,
  resolveBackground,
  type ResolvedBackground,
  type Tint,
} from "./background-layer";
import {
  livingScrim,
  resolveDrift,
  resolveLivingBackground,
  type BackgroundDrift,
  type LivingBackground,
  type LivingScrim,
} from "./living-background";
import { fillChangesInDark, ownColourCss } from "./colour-versions";
import { isLookSystem } from "./look-gate";
import { backgroundGradient } from "./visual-style";

type Attachment = NonNullable<PageBackgroundStyle["attachment"]>;

export type PageFill =
  | { kind: "none" }
  | { kind: "colour"; color: string }
  | { kind: "gradient"; gradient: NonNullable<ResolvedBackground["gradient"]> }
  | {
      kind: "mesh";
      preset: PageBackgroundMesh;
      colors: [string, string, string];
      strength: number;
      color?: string;
    }
  | {
      kind: "living";
      living: LivingBackground;
      scrim: LivingScrim;
      color?: string;
    }
  | {
      kind: "image";
      image: string;
      mobileImage?: string;
      position?: string;
      fit: "cover" | "tile";
      tint: Tint;
      overlayColor?: string;
    };

export interface ResolvedPageBackground {
  fill: PageFill;
  pattern?: {
    shape: PageBackgroundPattern;
    color: string;
    opacity: number;
    size: number;
  };
  grain?: "soft" | "strong";
  attachment: Attachment;
  /** On the look system, how the mesh, the pattern and the grain move. */
  drift?: BackgroundDrift;
}

export const MESH_PRESETS: readonly PageBackgroundMesh[] = [
  "aurora",
  "corners",
  "bloom",
];
export const PATTERN_SHAPES: readonly PageBackgroundPattern[] = [
  "dots",
  "grid",
  "lines",
  "diagonal",
  "checks",
  "waves",
];
const ATTACHMENTS: readonly Attachment[] = ["fixed", "scroll", "parallax"];

/** Theme colours that every website defines, so a preset without its own colours follows the website's Design. */
export const MESH_THEME_COLOURS: [string, string, string] = [
  "var(--button-bg-color)",
  "var(--testimony-quote-color)",
  "var(--testimony-bg-color)",
];

/** [x, y, width, height, colour] per colour field, in percent of the background. */
const MESH_FIELDS: Record<
  PageBackgroundMesh,
  [number, number, number, number, 0 | 1 | 2][]
> = {
  aurora: [
    [12, 0, 55, 45, 0],
    [55, 8, 50, 40, 1],
    [92, 0, 45, 50, 2],
    [40, 100, 70, 35, 0],
  ],
  corners: [
    [0, 0, 65, 60, 0],
    [100, 100, 65, 60, 1],
    [100, 0, 35, 35, 2],
  ],
  bloom: [
    [20, 28, 35, 35, 0],
    [78, 18, 30, 32, 1],
    [62, 76, 40, 40, 2],
    [8, 88, 26, 26, 1],
  ],
};

function clamp(value: unknown, min: number, max: number, fallback: number) {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.min(max, Math.max(min, value))
    : fallback;
}

function colour(value: unknown): string | undefined {
  const text = typeof value === "string" ? value.trim() : undefined;
  return isCssColour(text) ? text : undefined;
}

function fillOf(value: Record<string, unknown>, theme: unknown): PageFill {
  switch (value.kind) {
    case "living": {
      const living = isLookSystem(theme) && resolveLivingBackground(value);
      return living
        ? {
            kind: "living",
            living,
            scrim: livingScrim(theme, living),
            ...(living.color ? { color: living.color } : {}),
          }
        : { kind: "none" };
    }
    case "colour":
    case "gradient": {
      const layer = resolveBackground({
        kind: value.kind,
        color: value.color,
        gradient: value.gradient,
      });
      if (layer.kind === "colour" && isCssColour(layer.color))
        return { kind: "colour", color: layer.color };
      if (
        layer.kind === "gradient" &&
        layer.gradient?.steps.every((step) => isCssColour(step.color))
      )
        return { kind: "gradient", gradient: layer.gradient };
      return { kind: "none" };
    }
    case "mesh": {
      const mesh = record(value.mesh);
      const own = Array.isArray(mesh.colors) ? mesh.colors : [];
      return {
        kind: "mesh",
        preset:
          MESH_PRESETS.find((preset) => preset === mesh.preset) ?? "aurora",
        colors: MESH_THEME_COLOURS.map(
          (fallback, index) => colour(own[index]) ?? fallback,
        ) as [string, string, string],
        strength: clamp(mesh.strength, 10, 100, 45),
        ...(colour(value.color) ? { color: colour(value.color) } : {}),
      };
    }
    case "image": {
      const layer = resolveBackground({
        kind: "image",
        image: value.image,
        mobileImage: value.mobileImage,
        position: value.position,
        tint: value.tint,
        overlayColor: value.overlayColor,
      });
      if (layer.kind !== "image" || !layer.image) return { kind: "none" };
      return {
        kind: "image",
        image: layer.image,
        ...(layer.mobileImage ? { mobileImage: layer.mobileImage } : {}),
        ...(layer.position ? { position: layer.position } : {}),
        fit: value.fit === "tile" ? "tile" : "cover",
        tint: layer.tint,
        ...(layer.overlayColor ? { overlayColor: layer.overlayColor } : {}),
      };
    }
    default:
      return { kind: "none" };
  }
}

/** On a living background its pattern and grain move with it; elsewhere only what `drift` moves, if anything does. */
function driftOf(
  value: Record<string, unknown>,
  fill: PageFill,
  moving: boolean,
): BackgroundDrift | undefined {
  if (fill.kind === "living") {
    const { speed, phones, pause } = fill.living;
    return moving ? { speed, phones, pause } : undefined;
  }
  return moving || fill.kind === "mesh" ? resolveDrift(value.drift) : undefined;
}

/**
 * Never throws; undefined when there is nothing to draw, so the page looks exactly as without the key. Levende and
 * `drift` are read only for a `theme` on the look system.
 */
export function resolvePageBackground(
  input: unknown,
  theme?: unknown,
): ResolvedPageBackground | undefined {
  const value = record(input);
  const fill = fillOf(value, theme);
  const rawPattern = record(value.pattern);
  const shape = PATTERN_SHAPES.find((name) => name === rawPattern.shape);
  const pattern = shape
    ? {
        shape,
        color: colour(rawPattern.color) ?? "var(--page-text-color)",
        opacity: clamp(rawPattern.opacity, 2, 40, 8),
        size: clamp(rawPattern.size, 8, 96, 24),
      }
    : undefined;
  const grain =
    value.grain === "soft" || value.grain === "strong"
      ? value.grain
      : undefined;
  if (fill.kind === "none" && !pattern && !grain) return undefined;
  const drift = isLookSystem(theme)
    ? driftOf(value, fill, Boolean(pattern || grain))
    : undefined;
  return {
    fill,
    ...(pattern ? { pattern } : {}),
    ...(grain ? { grain } : {}),
    attachment:
      ATTACHMENTS.find((attachment) => attachment === value.attachment) ??
      "fixed",
    ...(drift ? { drift } : {}),
  };
}

/**
 * Sidens baggrund as the page paints it: on the look system an own colour code whose dark version `derived.darkOwn`
 * names takes it in dark as the fill's colour, a gradient stop or a mesh colour, and the pattern with it on a fill that
 * changes in dark; a picture, a living effect's colours and every old website keep theirs.
 */
export function paintedPageBackground(
  background: ResolvedPageBackground | undefined,
  theme: unknown,
): ResolvedPageBackground | undefined {
  if (!background || !isLookSystem(theme)) return background;
  const own = (colour: string) => ownColourCss(theme, colour);
  const { fill, pattern } = background;
  const painted: PageFill =
    fill.kind === "colour"
      ? { ...fill, color: own(fill.color) }
      : fill.kind === "gradient"
        ? {
            ...fill,
            gradient: {
              ...fill.gradient,
              steps: fill.gradient.steps.map((step) => ({
                ...step,
                color: own(step.color),
              })),
            },
          }
        : fill.kind === "mesh"
          ? {
              ...fill,
              colors: fill.colors.map(own) as [string, string, string],
              ...(fill.color ? { color: own(fill.color) } : {}),
            }
          : fill.kind === "living" && fill.color
            ? { ...fill, color: own(fill.color) }
            : fill;
  const changes =
    fill.kind === "gradient"
      ? fill.gradient.steps.every((step) =>
          fillChangesInDark(theme, step.color),
        )
      : (fill.kind === "colour" ||
          fill.kind === "mesh" ||
          fill.kind === "living") &&
        (!fill.color || fillChangesInDark(theme, fill.color));
  return {
    ...background,
    fill: painted,
    ...(pattern && (fill.kind === "none" || changes)
      ? { pattern: { ...pattern, color: own(pattern.color) } }
      : {}),
  };
}

/** The page's own `layout.background` when it has one, else the website's. */
export function pageBackgroundOf(
  cmsData: (Pick<CMSData, "layout"> & { theme?: unknown }) | undefined,
  page: Pick<CmsPage, "layout"> | undefined,
): ResolvedPageBackground | undefined {
  return resolvePageBackground(
    page?.layout?.background ?? cmsData?.layout?.background,
    cmsData?.theme,
  );
}

export function meshImage(fill: Extract<PageFill, { kind: "mesh" }>): string {
  return MESH_FIELDS[fill.preset]
    .map(
      ([x, y, width, height, slot]) =>
        `radial-gradient(${width}% ${height}% at ${x}% ${y}%, color-mix(in srgb, ${fill.colors[slot]} ${fill.strength}%, transparent), transparent)`,
    )
    .join(", ");
}

export function fillImage(fill: PageFill): string | undefined {
  if (fill.kind === "gradient") return backgroundGradient(fill.gradient);
  if (fill.kind === "mesh") return meshImage(fill);
  return undefined;
}

/** The plain colour the page shows, for the fades and surfaces that paint »in the page colour«. */
export function pageBackgroundVariables(
  background: ResolvedPageBackground | undefined,
): CSSProperties | undefined {
  const fill = background?.fill;
  const color =
    fill?.kind === "colour" || fill?.kind === "mesh" || fill?.kind === "living"
      ? fill.color
      : undefined;
  return color ? ({ "--page-bg-color": color } as CSSProperties) : undefined;
}

/** What automatic text tone sees below a section: the colour, gradient or picture; a pattern or grain does not count. */
export function pageToneBackground(
  background: ResolvedPageBackground | undefined,
): ResolvedBackground {
  const fill = background?.fill;
  switch (fill?.kind) {
    case "colour":
      return { kind: "colour", color: fill.color, tint: "none" };
    case "gradient":
      return { kind: "gradient", gradient: fill.gradient, tint: "none" };
    case "mesh":
    case "living":
      return fill.color
        ? { kind: "colour", color: fill.color, tint: "none" }
        : { kind: "none", tint: "none" };
    case "image":
      return {
        kind: "image",
        image: fill.image,
        tint: fill.tint,
        overlayColor: fill.overlayColor,
      };
    default:
      return { kind: "none", tint: "none" };
  }
}
